// Trigger conditions — the 14 live types the shipping LoadCondition switch constructs
// (the UNDONE-#ifdef'd ones never parse). Each Init consumes its comma-separated arg
// string through the same IniScanf path as the C++; IsTrue evaluates against sim state.
// game/TriggerConditions.cpp (all 590 lines).

import { GetGameVar } from '../sim/gamevar.ts';
import type { SimContext } from '../context.ts';
import { IniScanf } from '../data/ini.ts';
import {
  Trigger,
  QualifiedNumber,
  ParseNumber,
  ParseUnitMask,
  ParseUpgradeMask,
  ParseString,
  GetSideMaskFromCaSideMask,
  GetPlayersListFromCaSideMask,
  knMissionLoadedCondition,
  knCreditsCondition,
  knOwnsUnitsCondition,
  knAreaContainsUnitsCondition,
  knPlaceStructureModeCondition,
  knMinerCantFindGalaxiteCondition,
  knGalaxiteCapacityReachedCondition,
  knElapsedTimeCondition,
  knSwitchCondition,
  knPeriodicTimerCondition,
  knDiscoversSideCondition,
  knCountdownTimerCondition,
  knTestPvarCondition,
  knHasUpgradesCondition,
  knQualifierAtLeast,
  knQualifierExactly,
} from './triggermgr.ts';
import { Enum } from '../types.ts';

export abstract class Condition {
  Init(_ctx: SimContext, _psz: string): boolean {
    return true;
  }
  abstract IsTrue(ctx: SimContext, side: number): boolean;
}

// TriggerConditions.cpp:5-119 — the LoadCondition switch
export function LoadCondition(ctx: SimContext, ptgr: Trigger, pszValue: string): boolean {
  const r = IniScanf(pszValue, '%d,%s');
  if (r.count === 0) return false;
  const nCondition = r.values[0] as number;
  const sz = (r.count >= 2 ? r.values[1] : '') as string;

  let pcdn: Condition;
  switch (nCondition) {
    case knMissionLoadedCondition:
      pcdn = new MissionLoadedCondition();
      break;
    case knCreditsCondition:
      pcdn = new CreditsCondition();
      break;
    case knOwnsUnitsCondition:
      pcdn = new OwnsUnitsCondition();
      break;
    case knAreaContainsUnitsCondition:
      pcdn = new AreaContainsUnitsCondition(ctx);
      break;
    case knPlaceStructureModeCondition:
      pcdn = new PlaceStructureModeCondition();
      break;
    case knMinerCantFindGalaxiteCondition:
      pcdn = new MinerCantFindGalaxiteCondition();
      break;
    case knGalaxiteCapacityReachedCondition:
      pcdn = new GalaxiteCapacityReachedCondition();
      break;
    case knElapsedTimeCondition:
      pcdn = new ElapsedTimeCondition();
      break;
    case knSwitchCondition:
      pcdn = new SwitchCondition();
      break;
    case knPeriodicTimerCondition:
      pcdn = new PeriodicTimerCondition();
      break;
    case knDiscoversSideCondition:
      pcdn = new DiscoversSideCondition();
      break;
    case knCountdownTimerCondition:
      pcdn = new CountdownTimerCondition();
      break;
    case knTestPvarCondition:
      pcdn = new TestPvarCondition();
      break;
    case knHasUpgradesCondition:
      pcdn = new HasUpgradesCondition();
      break;
    default:
      throw new Error(`LoadCondition: unknown condition type ${nCondition} (C++ asserts)`);
  }

  if (!pcdn.Init(ctx, sz)) return false;
  ptgr.m_pcdn.push(pcdn); // link in last
  return true;
}

// TriggerConditions.cpp:139-152
export class MissionLoadedCondition extends Condition {
  override IsTrue(): boolean {
    return true;
  }
}

// TriggerConditions.cpp:154-184
export class CreditsCondition extends Condition {
  m_wfCaSideMask = 0;
  m_qnum = new QualifiedNumber();

  override Init(_ctx: SimContext, psz: string): boolean {
    const pp = { psz };
    const out = { n: 0 };
    if (!ParseNumber(pp, out)) return false;
    this.m_wfCaSideMask = out.n;
    return this.m_qnum.Parse(pp);
  }

  override IsTrue(ctx: SimContext, side: number): boolean {
    const applr = GetPlayersListFromCaSideMask(ctx, side, this.m_wfCaSideMask);
    let cCredits = 0;
    for (const p of applr) cCredits += p.GetCredits();
    return this.m_qnum.Compare(cCredits);
  }
}

// TriggerConditions.cpp:186-219
export class OwnsUnitsCondition extends Condition {
  m_wfCaSideMask = 0;
  m_qnum = new QualifiedNumber();
  m_um = 0;

  override Init(_ctx: SimContext, psz: string): boolean {
    const pp = { psz };
    const out = { n: 0 };
    if (!ParseNumber(pp, out)) return false;
    this.m_wfCaSideMask = out.n;
    if (!this.m_qnum.Parse(pp)) return false;
    if (!ParseUnitMask(pp, out)) return false;
    this.m_um = out.n;
    return true;
  }

  override IsTrue(ctx: SimContext, side: number): boolean {
    const applr = GetPlayersListFromCaSideMask(ctx, side, this.m_wfCaSideMask);
    let cunt = 0;
    for (const p of applr) cunt += p.GetUnitActiveCountFromMask(this.m_um);
    return this.m_qnum.Compare(cunt);
  }
}

// TriggerConditions.cpp:221-279 — including the version-0 backwards-compat arms (sim-quirks A11)
export class AreaContainsUnitsCondition extends Condition {
  m_nVersionLevel: number;
  m_nArea = 0;
  m_qnum = new QualifiedNumber();
  m_um = 0;
  m_wfCaSideMask = 0;

  constructor(ctx: SimContext) {
    super();
    this.m_nVersionLevel = ctx.sim!.GetLevel()!.GetVersion();
  }

  override Init(_ctx: SimContext, psz: string): boolean {
    const pp = { psz };
    const out = { n: 0 };
    if (!ParseNumber(pp, out)) return false;
    this.m_nArea = out.n;
    if (!this.m_qnum.Parse(pp)) return false;
    if (!ParseUnitMask(pp, out)) return false;
    this.m_um = out.n;
    if (!ParseNumber(pp, out)) return false;
    this.m_wfCaSideMask = out.n;
    return true;
  }

  override IsTrue(ctx: SimContext, side: number): boolean {
    const sidm = GetSideMaskFromCaSideMask(ctx, side, this.m_wfCaSideMask);

    // Version-0 levels keep CheckUnitsInArea's any-type-AND-any-side logic error
    if (this.m_nVersionLevel === 0) {
      switch (this.m_qnum.m_nNumber) {
        case 0:
          if (this.m_qnum.m_nQualifier === knQualifierExactly) {
            return !ctx.gobm.CheckUnitsInArea(this.m_nArea, sidm, this.m_um);
          }
          break;
        case 1:
          if (this.m_qnum.m_nQualifier === knQualifierAtLeast) {
            return ctx.gobm.CheckUnitsInArea(this.m_nArea, sidm, this.m_um);
          }
          break;
      }
    }

    const enm = new Enum();
    let cgobsMatch = 0;
    while (ctx.gobm.EnumGobsInArea(enm, this.m_nArea, sidm, this.m_um) !== null) cgobsMatch++;
    return this.m_qnum.Compare(cgobsMatch);
  }
}

// TriggerConditions.cpp:290-301 — reads the SP place-structure UI mode (gpfrmPlace
// owner); ctx.fPlaceStructureMode is the embedder seam (SP-only, single machine)
export class PlaceStructureModeCondition extends Condition {
  override IsTrue(ctx: SimContext): boolean {
    return ctx.fPlaceStructureMode;
  }
}

// TriggerConditions.cpp:303-329 — one-update condition latched by the Miner
export class MinerCantFindGalaxiteCondition extends Condition {
  m_wfCaSideMask = 0;

  override Init(_ctx: SimContext, psz: string): boolean {
    const pp = { psz };
    const out = { n: 0 };
    if (!ParseNumber(pp, out)) return false;
    this.m_wfCaSideMask = out.n;
    return true;
  }

  override IsTrue(ctx: SimContext, side: number): boolean {
    const sidm = GetSideMaskFromCaSideMask(ctx, side, this.m_wfCaSideMask);
    return ctx
      .sim!.GetLevel()!
      .GetTriggerMgr()
      .IsConditionTrue(knMinerCantFindGalaxiteCondition, sidm);
  }
}

// TriggerConditions.cpp:331-357 — one-update condition latched by the Processor
export class GalaxiteCapacityReachedCondition extends Condition {
  m_wfCaSideMask = 0;

  override Init(_ctx: SimContext, psz: string): boolean {
    const pp = { psz };
    const out = { n: 0 };
    if (!ParseNumber(pp, out)) return false;
    this.m_wfCaSideMask = out.n;
    return true;
  }

  override IsTrue(ctx: SimContext, side: number): boolean {
    const sidm = GetSideMaskFromCaSideMask(ctx, side, this.m_wfCaSideMask);
    return ctx
      .sim!.GetLevel()!
      .GetTriggerMgr()
      .IsConditionTrue(knGalaxiteCapacityReachedCondition, sidm);
  }
}

// TriggerConditions.cpp:359-377 — elapsed seconds = GetTickCount()/100
export class ElapsedTimeCondition extends Condition {
  m_qnum = new QualifiedNumber();

  override Init(_ctx: SimContext, psz: string): boolean {
    return this.m_qnum.Parse({ psz });
  }

  override IsTrue(ctx: SimContext): boolean {
    return this.m_qnum.Compare(Math.trunc(ctx.sim!.GetTickCount() / 100));
  }
}

// TriggerConditions.cpp:387-417
export class SwitchCondition extends Condition {
  m_iSwitch = 0;
  m_fOn = false;

  override Init(_ctx: SimContext, psz: string): boolean {
    const pp = { psz };
    const out = { n: 0 };
    if (!ParseNumber(pp, out)) return false;
    this.m_iSwitch = out.n;
    if (!ParseNumber(pp, out)) return false;
    this.m_fOn = out.n === 1;
    return true;
  }

  override IsTrue(ctx: SimContext): boolean {
    return ctx.sim!.GetLevel()!.GetTriggerMgr().GetSwitch(this.m_iSwitch) === this.m_fOn;
  }
}

// TriggerConditions.cpp:419-443 — the timer registers at PARSE time; it only starts
// counting the first time it's tested (deterministic firing under prior conditions)
export class PeriodicTimerCondition extends Condition {
  m_iTimer = 0;

  override Init(ctx: SimContext, psz: string): boolean {
    const pp = { psz };
    const out = { n: 0 };
    if (!ParseNumber(pp, out)) return false;
    this.m_iTimer = ctx
      .sim!.GetLevel()!
      .GetTriggerMgr()
      .AddPeriodicTimer(out.n * 100);
    return true;
  }

  override IsTrue(ctx: SimContext): boolean {
    const ptgrm = ctx.sim!.GetLevel()!.GetTriggerMgr();
    ptgrm.StartPeriodicTimer(this.m_iTimer);
    return ptgrm.IsPeriodicTimerTriggered(this.m_iTimer);
  }
}

// TriggerConditions.cpp:463-477
export class CountdownTimerCondition extends Condition {
  m_qnum = new QualifiedNumber();

  override Init(_ctx: SimContext, psz: string): boolean {
    return this.m_qnum.Parse({ psz });
  }

  override IsTrue(ctx: SimContext): boolean {
    const { secs, running } = ctx.sim!.GetLevel()!.GetTriggerMgr().GetCountdownTimer().GetTimer();
    if (running) return this.m_qnum.Compare(secs);
    return false;
  }
}

// TriggerConditions.cpp:503-533
export class DiscoversSideCondition extends Condition {
  m_wfCaSideMaskA = 0;
  m_wfCaSideMaskB = 0;

  override Init(_ctx: SimContext, psz: string): boolean {
    const pp = { psz };
    const out = { n: 0 };
    if (!ParseNumber(pp, out)) return false;
    this.m_wfCaSideMaskA = out.n;
    if (!ParseNumber(pp, out)) return false;
    this.m_wfCaSideMaskB = out.n;
    return true;
  }

  override IsTrue(ctx: SimContext, side: number): boolean {
    let sidmTotalDiscovered = 0;
    const applr = GetPlayersListFromCaSideMask(ctx, side, this.m_wfCaSideMaskA);
    for (const p of applr) sidmTotalDiscovered |= p.GetDiscoveredSides();
    return (sidmTotalDiscovered & GetSideMaskFromCaSideMask(ctx, side, this.m_wfCaSideMaskB)) !== 0;
  }
}

// TriggerConditions.cpp:535-559 — persistent profile variables (ctx.pvars; SP scripting)
export class TestPvarCondition extends Condition {
  m_szName = '';
  m_qnum = new QualifiedNumber();

  override Init(_ctx: SimContext, psz: string): boolean {
    const pp = { psz };
    const out = { s: '' };
    if (!ParseString(pp, out)) return false;
    this.m_szName = out.s;
    return this.m_qnum.Parse(pp);
  }

  override IsTrue(ctx: SimContext): boolean {
    // TriggerConditions.cpp:554 goes through Game::GetVar — '$' sysvars resolve
    // BEFORE the dictionary, and a successful dictionary read RELINKS (sim-visible
    // through the next save's pvar order).
    const szT = GetGameVar(ctx, this.m_szName);
    const nValue = szT !== null ? parseInt(szT, 10) | 0 || 0 : 0;
    return this.m_qnum.Compare(nValue);
  }
}

// TriggerConditions.cpp:561-589
export class HasUpgradesCondition extends Condition {
  m_wfCaSideMask = 0;
  m_upgm = 0;

  override Init(_ctx: SimContext, psz: string): boolean {
    const pp = { psz };
    const out = { n: 0 };
    if (!ParseNumber(pp, out)) return false;
    this.m_wfCaSideMask = out.n;
    if (!ParseUpgradeMask(pp, out)) return false;
    this.m_upgm = out.n;
    return true;
  }

  override IsTrue(ctx: SimContext, side: number): boolean {
    let upgm = 0;
    const applr = GetPlayersListFromCaSideMask(ctx, side, this.m_wfCaSideMask);
    for (const p of applr) upgm |= p.GetUpgradeMask();
    return (upgm & this.m_upgm) !== 0;
  }
}
